import { styled, Typography } from '@mui/material';
import { MentionsPopupState, RichTextEditorFile } from '@provision/rich-text-editor';
import { MutableRefObject, useCallback, useState } from 'react';
import { Subject } from 'rxjs';
import { MentionPopup } from 'src/components/MentionPopup/mention-popup';
import RichTextEditor from 'src/components/RichTextEditor/rich-text-editor';
import { privateNoteButtonStyles } from 'src/pages/Conversations/Chat/shared-styles';
import { EditableMessageAttachments } from './editable-message-attachments';

interface ChatMessageEditModeProps {
  onClose: () => void;
  onSave: (newContent: string, newAttachments: RichTextEditorFile[]) => void;
  content: string;
  attachments?: RichTextEditorFile[];
  partnerId: number | null;
}

const ChatMessageEditModeWrapper = styled(Typography, {
  name: 'ChatMessageEditModeWrapper',
})(({ theme }) => ({
  ...privateNoteButtonStyles(theme),
  // Hide the rich-text-editor package's default attachment thumbnails so we can
  // render our own compact removable pills (EditableMessageAttachments) via the
  // editor's contentFooter slot. `.file-list-thumbnails-container` is the
  // package's documented styling hook — FileItemThumbnail is "based on native css
  // classes so they could be re-written by parent's apps if needed". Scoped to
  // this edit wrapper so every other editor instance keeps the package default.
  '& .file-list-thumbnails-container': {
    display: 'none',
  },
}));

export const ChatMessageEditMode = ({
  onClose,
  onSave,
  content,
  attachments,
  partnerId,
}: ChatMessageEditModeProps) => {
  const [files, setFiles] = useState<RichTextEditorFile[]>(attachments || []);

  const handleOnSubmit = useCallback(
    (newContent: string) => {
      onSave(newContent, files);
    },
    [onSave, files],
  );

  const handleRemoveAttachment = useCallback((index: number) => {
    setFiles((prev) => prev.toSpliced(index, 1));
  }, []);

  const handleGetMentionPopup = useCallback(
    (
      mentionUpdatePopupStateSubject: Subject<Partial<MentionsPopupState>>,
      onMentionKeyDownRef: MutableRefObject<((event: KeyboardEvent) => boolean) | null>,
    ) => (
      <MentionPopup
        onMentionKeyDownRef={onMentionKeyDownRef}
        updatePopupStateSubject={mentionUpdatePopupStateSubject}
        partnerId={partnerId}
      />
    ),
    [partnerId],
  );

  return (
    <ChatMessageEditModeWrapper marginTop={2} marginBottom={2}>
      <RichTextEditor
        content={content}
        onSubmit={handleOnSubmit}
        buttonText="Save"
        primaryButtonIcon="save"
        secondaryButtonIcon="close"
        secondaryButtonText="Cancel"
        secondaryButtonTooltipText="Cancel editing"
        handleSecondaryButton={onClose}
        watchContent
        files={files}
        setFiles={setFiles}
        slots={{
          contentFooter: (
            <EditableMessageAttachments files={files} onRemove={handleRemoveAttachment} />
          ),
        }}
        onGetMentionPopup={handleGetMentionPopup}
      />
    </ChatMessageEditModeWrapper>
  );
};
